/**
 * TrackUploadPage - Step 6: Upload track with strict requirements
 * Requires: 3000x3000 minimum album cover, full metadata credits
 */

import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Music, Image, ArrowLeft, ArrowRight, Check, AlertCircle,
  User, Users, Mic2, Guitar, X, Plus, Calendar, Info
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GENRES = [
  { slug: 'hip-hop', label: 'Hip-Hop' },
  { slug: 'rnb', label: 'R&B' },
  { slug: 'gospel-hip-hop', label: 'Gospel Hip Hop' },
  { slug: 'gospel', label: 'Gospel' },
  { slug: 'afrobeats', label: 'Afrobeats' },
  { slug: 'country', label: 'Country' },
  { slug: 'pop', label: 'Pop' },
  { slug: 'reggae-dancehall', label: 'Reggae/Dancehall' },
];

const MIN_ARTWORK_SIZE = 3000;

// Credit input component
const CreditInput = ({ label, icon: Icon, credits, setCredits, placeholder }) => {
  const [inputValue, setInputValue] = useState('');

  const addCredit = () => {
    if (inputValue.trim() && !credits.includes(inputValue.trim())) {
      setCredits([...credits, inputValue.trim()]);
      setInputValue('');
    }
  };

  const removeCredit = (index) => {
    setCredits(credits.filter((_, i) => i !== index));
  };

  return (
    <div>
      <label className="block text-[#A1A1AA] text-sm mb-2 flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </label>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCredit())}
          placeholder={placeholder}
          className="flex-1 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] text-sm"
        />
        <button
          type="button"
          onClick={addCredit}
          className="px-4 py-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {credits.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {credits.map((credit, index) => (
            <span
              key={index}
              className="px-3 py-1 rounded-full bg-[#00F0FF]/20 text-[#00F0FF] text-sm flex items-center gap-2"
            >
              {credit}
              <button
                type="button"
                onClick={() => removeCredit(index)}
                className="hover:text-white transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const TrackUploadPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const audioInputRef = useRef(null);
  const artworkInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  // Step 1: Audio file
  const [audioFile, setAudioFile] = useState(null);
  const [audioData, setAudioData] = useState(null);
  const [audioDuration, setAudioDuration] = useState(null);

  // Step 2: Artwork
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkData, setArtworkData] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(null);
  const [artworkDimensions, setArtworkDimensions] = useState({ width: 0, height: 0 });
  const [artworkError, setArtworkError] = useState('');

  // Step 3: Track info
  const [trackTitle, setTrackTitle] = useState('');
  const [genre, setGenre] = useState('');
  const [explicit, setExplicit] = useState(false);
  const [releaseDate, setReleaseDate] = useState('');
  const [isrc, setIsrc] = useState('');

  // Step 4: Credits
  const [songwriters, setSongwriters] = useState([]);
  const [composers, setComposers] = useState([]);
  const [producers, setProducers] = useState([]);
  const [vocalists, setVocalists] = useState([]);

  // Handle audio file selection
  const handleAudioSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (MIME can be empty/odd on mobile, so also allow by extension)
    const audioExts = ['.mp3', '.wav', '.flac', '.m4a', '.aac', '.ogg', '.aiff'];
    const nameLower = (file.name || '').toLowerCase();
    const isAudioByExt = audioExts.some((ext) => nameLower.endsWith(ext));
    if (!file.type.startsWith('audio/') && !isAudioByExt) {
      setError('Please select a valid audio file (MP3, WAV, FLAC, M4A, AAC)');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('Audio file must be under 50MB');
      return;
    }

    setAudioFile(file);
    setError('');

    // Get duration
    const audio = new Audio();
    audio.src = URL.createObjectURL(file);
    audio.onloadedmetadata = () => {
      setAudioDuration(audio.duration);
    };

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      setAudioData(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  // Handle artwork selection — auto-resize to a clean 3000x3000 square so uploads never fail
  const handleArtworkSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setArtworkError('Please select a valid image file');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setArtworkError('Image must be under 20MB');
      return;
    }

    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const size = MIN_ARTWORK_SIZE; // 3000
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#0B0A10';
      ctx.fillRect(0, 0, size, size);
      // center "cover" crop
      const scale = Math.max(size / img.naturalWidth, size / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);

      setArtworkDimensions({ width: size, height: size });
      setArtworkData(dataUrl);
      setArtworkPreview(dataUrl);
      setArtworkFile(file);
      setArtworkError('');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setArtworkError('Could not read that image. Please try another.');
    };
    img.src = url;
  }, []);

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Submit track
  const handleSubmit = async () => {
    if (!audioData || !trackTitle.trim()) {
      setError('Please complete all required fields');
      return;
    }
    if (!genre) {
      setError('Please select a genre for this release.');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/auth');
        return;
      }

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      // Upload audio directly to R2 via presigned URL (bypasses proxy body-size limits
      // that break large base64 uploads). Falls back to base64 if no File is present.
      let audioUrl = null;
      if (audioFile) {
        const ct = audioFile.type || 'audio/mpeg';
        const presign = await axios.get(`${API_URL}/api/radio/media/presigned-upload`, {
          params: { filename: audioFile.name || 'track.mp3', media_type: 'audio', content_type: ct },
          headers: { Authorization: `Bearer ${token}` }
        });
        const putRes = await fetch(presign.data.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': ct },
          body: audioFile,
        });
        if (!putRes.ok) {
          clearInterval(progressInterval);
          throw new Error('Audio upload to storage failed. Please try again.');
        }
        audioUrl = presign.data.public_url;
      }

      const response = await axios.post(
        `${API_URL}/api/artists/tracks/upload`,
        {
          track_title: trackTitle.trim(),
          audio_url: audioUrl || undefined,
          audio_data: audioUrl ? undefined : audioData,
          artwork: artworkData || undefined,
          artwork_width: artworkDimensions.width || undefined,
          artwork_height: artworkDimensions.height || undefined,
          genre: genre,
          songwriters,
          composers,
          producers,
          vocalists,
          explicit,
          release_date: releaseDate || undefined,
          isrc: isrc || undefined
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.data.success) {
        // Show success and redirect
        setTimeout(() => {
          navigate('/artist-dashboard');
        }, 1500);
      }
    } catch (err) {
      console.error('Upload error:', err);
      let errorMessage = 'Failed to upload track';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || String(e)).join(', ');
        }
      }
      setError(errorMessage);
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  // Check if step is valid
  const isStepValid = (stepNum) => {
    switch (stepNum) {
      case 1:
        return !!audioData;
      case 2:
        return true; // Artwork is recommended but not strictly required
      case 3:
        return trackTitle.trim().length > 0;
      case 4:
        return true; // Credits are optional
      default:
        return false;
    }
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              Upload Track
            </h1>
            <p className="text-[#71717A] text-sm">Step {step} of 4</p>
          </div>

          {/* Progress */}
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-1.5 w-6 rounded-full transition-all ${
                  s === step 
                    ? 'bg-gradient-to-r from-[#FF007F] to-[#00F0FF]'
                    : s < step 
                      ? 'bg-[#FF007F]'
                      : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Audio Upload */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>
                Upload Your Track
              </h2>
              <p className="text-[#A1A1AA] mb-8">
                Select your audio file (MP3, WAV, FLAC - max 50MB)
              </p>

              {/* No `accept` filter: iOS Safari incorrectly greys out audio files
                  when accept="audio/*" is set. We allow any file here and validate
                  it's audio (by MIME or extension) in handleAudioSelect. */}
              <input
                ref={audioInputRef}
                type="file"
                onChange={handleAudioSelect}
                className="hidden"
              />

              {!audioFile ? (
                <button
                  onClick={() => audioInputRef.current?.click()}
                  className="w-full aspect-video rounded-2xl border-2 border-dashed border-white/20 hover:border-[#00F0FF]/50 transition-colors flex flex-col items-center justify-center gap-4"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
                    <Music className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-medium">Click to select audio file</p>
                    <p className="text-[#71717A] text-sm">MP3, WAV, FLAC up to 50MB</p>
                  </div>
                </button>
              ) : (
                <div className="p-6 rounded-2xl bg-[#12101A] border border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#FF007F] to-[#00F0FF] flex items-center justify-center">
                      <Music className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{audioFile.name}</p>
                      <p className="text-[#71717A] text-sm">
                        {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                        {audioDuration && ` • ${formatDuration(audioDuration)}`}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setAudioFile(null);
                        setAudioData(null);
                        setAudioDuration(null);
                      }}
                      className="p-2 rounded-lg hover:bg-white/10 text-[#71717A] hover:text-white transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <p className="mt-4 text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </p>
              )}

              <button
                onClick={() => setStep(2)}
                disabled={!audioData}
                className="w-full mt-8 py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {/* Step 2: Artwork */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>
                Album Artwork
              </h2>
              <p className="text-[#A1A1AA] mb-2">
                Upload your cover art — we'll auto-crop it to a perfect {MIN_ARTWORK_SIZE}x{MIN_ARTWORK_SIZE} square
              </p>
              
              <div className="mb-6 p-3 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex items-start gap-3">
                <Info className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                <p className="text-[#00F0FF] text-sm">
                  Any image works — we automatically resize and center-crop it to the {MIN_ARTWORK_SIZE}x{MIN_ARTWORK_SIZE} square that streaming platforms require. For best quality, use a large, high-resolution image.
                </p>
              </div>

              <input
                ref={artworkInputRef}
                type="file"
                accept="image/*"
                onChange={handleArtworkSelect}
                className="hidden"
              />

              {!artworkPreview ? (
                <button
                  onClick={() => artworkInputRef.current?.click()}
                  className="w-full aspect-square max-w-sm mx-auto rounded-2xl border-2 border-dashed border-white/20 hover:border-[#00F0FF]/50 transition-colors flex flex-col items-center justify-center gap-4"
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#FF007F] flex items-center justify-center">
                    <Image className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="text-white font-medium">Click to select artwork</p>
                    <p className="text-[#71717A] text-sm">Min {MIN_ARTWORK_SIZE}x{MIN_ARTWORK_SIZE}px, square</p>
                  </div>
                </button>
              ) : (
                <div className="max-w-sm mx-auto">
                  <div className="relative aspect-square rounded-2xl overflow-hidden">
                    <img
                      src={artworkPreview}
                      alt="Artwork preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => {
                        setArtworkFile(null);
                        setArtworkData(null);
                        setArtworkPreview(null);
                        setArtworkDimensions({ width: 0, height: 0 });
                      }}
                      className="absolute top-3 right-3 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="mt-3 text-center text-[#A1A1AA] text-sm">
                    {artworkDimensions.width}x{artworkDimensions.height} pixels
                    {artworkDimensions.width >= MIN_ARTWORK_SIZE && (
                      <span className="text-green-400 ml-2">✓ Valid</span>
                    )}
                  </p>
                </div>
              )}

              {artworkError && (
                <p className="mt-4 text-red-400 text-sm flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {artworkError}
                </p>
              )}

              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-4 rounded-xl border border-white/10 text-white hover:bg-white/5 transition-colors"
                >
                  Skip for now
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={artworkError}
                  className="flex-1 py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Track Info */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>
                Track Details
              </h2>
              <p className="text-[#A1A1AA] mb-8">
                Add your track information
              </p>

              <div className="space-y-6">
                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">
                    Track Title *
                  </label>
                  <input
                    type="text"
                    value={trackTitle}
                    onChange={(e) => setTrackTitle(e.target.value)}
                    placeholder="Enter track title"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
                    data-testid="track-title-input"
                  />
                </div>

                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">
                    Genre <span className="text-[#FF007F]">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {GENRES.map((g) => (
                      <button
                        key={g.slug}
                        type="button"
                        data-testid={`genre-option-${g.slug}`}
                        onClick={() => setGenre(genre === g.slug ? '' : g.slug)}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          genre === g.slug
                            ? 'bg-[#FF007F] text-white'
                            : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                        }`}
                      >
                        {g.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={explicit}
                      onChange={(e) => setExplicit(e.target.checked)}
                      className="w-5 h-5 rounded border-white/20 bg-white/5 text-[#FF007F] focus:ring-[#FF007F]"
                    />
                    <span className="text-white">Explicit Content</span>
                  </label>
                </div>

                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Release Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={releaseDate}
                    onChange={(e) => setReleaseDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-[#00F0FF]"
                  />
                </div>

                <div>
                  <label className="block text-[#A1A1AA] text-sm mb-2">
                    ISRC (Optional)
                  </label>
                  <input
                    type="text"
                    value={isrc}
                    onChange={(e) => setIsrc(e.target.value)}
                    placeholder="e.g., USRC17607839"
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
                  />
                </div>
              </div>

              <button
                onClick={() => setStep(4)}
                disabled={!trackTitle.trim()}
                className="w-full mt-8 py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Credits
                <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {/* Step 4: Credits */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>
                Track Credits
              </h2>
              <p className="text-[#A1A1AA] mb-8">
                Add credits for everyone who worked on this track
              </p>

              <div className="space-y-6">
                <CreditInput
                  label="Songwriters"
                  icon={User}
                  credits={songwriters}
                  setCredits={setSongwriters}
                  placeholder="Add songwriter name"
                />

                <CreditInput
                  label="Composers"
                  icon={Music}
                  credits={composers}
                  setCredits={setComposers}
                  placeholder="Add composer name"
                />

                <CreditInput
                  label="Producers"
                  icon={Guitar}
                  credits={producers}
                  setCredits={setProducers}
                  placeholder="Add producer name"
                />

                <CreditInput
                  label="Vocalists"
                  icon={Mic2}
                  credits={vocalists}
                  setCredits={setVocalists}
                  placeholder="Add vocalist name"
                />
              </div>

              {error && (
                <p className="mt-4 text-red-400 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </p>
              )}

              {/* Upload Progress */}
              {loading && (
                <div className="mt-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Uploading...</span>
                    <span className="text-white">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF]"
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Success State */}
              {uploadProgress === 100 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="mt-6 p-6 rounded-2xl bg-green-500/10 border border-green-500/20 text-center"
                >
                  <Check className="w-12 h-12 mx-auto mb-3 text-green-400" />
                  <p className="text-white font-semibold">Track Uploaded Successfully!</p>
                  <p className="text-[#A1A1AA] text-sm mt-1">Redirecting to dashboard...</p>
                </motion.div>
              )}

              <button
                onClick={handleSubmit}
                disabled={loading || uploadProgress === 100}
                className="w-full mt-8 py-4 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-upload-btn"
              >
                {loading ? (
                  <>
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                    />
                    Uploading...
                  </>
                ) : uploadProgress === 100 ? (
                  <>
                    <Check className="w-5 h-5" />
                    Uploaded!
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload Track
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default TrackUploadPage;
